/**
 * live_statsオブジェクトから最高同時視聴者数を取得する
 * @param liveStats - タイムスタンプごとの同時視聴者数を格納したオブジェクト
 * @returns 最高同時視聴者数。liveStatsが存在しない場合はundefined
 */
export function getMaxConcurrentViewers(liveStats: Record<string, number> | null | undefined): number | undefined {
  if (!liveStats || Object.keys(liveStats).length === 0) {
      return undefined;
  }
  
  return Math.max(...Object.values(liveStats));
}

/**
* live_statsから最新の同時視聴者数を取得する
* @param liveStats - タイムスタンプごとの同時視聴者数を格納したオブジェクト
* @returns 最新の同時視聴者数。liveStatsが存在しない場合はundefined
*/
export function getLatestConcurrentViewers(liveStats: Record<string, number> | null | undefined): number | undefined {
  if (!liveStats || Object.keys(liveStats).length === 0) {
      return undefined;
  }
  
  const latestTimestamp = Object.keys(liveStats).sort().pop();
  return latestTimestamp ? liveStats[latestTimestamp] : undefined;
}

/**
* アーカイブ動画の最高同時接続数を取得する
* @param videos - アーカイブ動画の配列
* @returns 最高同時接続数
*/
export function getMaxConcurrentViewersFromVideos(videos: any[] | undefined): number {
if (!videos) return 0;
const maxConcurrentViewers = videos.reduce((max, video) => {
  const videoMax = getMaxConcurrentViewers(video.live_stats);
  return videoMax > max ? videoMax : max;
}, 0);
return maxConcurrentViewers;
}

/**
* アーカイブ動画の平均同時接続数を取得する
* @param videos - アーカイブ動画の配列
* @returns 平均同時接続数
*/
export function getAvgConcurrentViewers(videos: any[] | undefined): number {
if (!videos || videos.length === 0) return 0;

let totalViewers = 0;
let validVideoCount = 0;

videos.forEach(video => {
  if (video.live_stats) {
    const values = Object.values(video.live_stats).filter(val => Number(val) > 0);
    if (values.length > 0) {
      totalViewers += values.reduce((sum: number, val: number) => sum + Number(val), 0) / values.length;
      validVideoCount++;
    }
  }
});

return validVideoCount > 0 ? Math.round(totalViewers / validVideoCount) : 0;
}

/**
* アーカイブ動画の平均配信時間を取得する
* @param videos - アーカイブ動画の配列
* @returns 平均配信時間（秒）
*/
export function getAvgStreamDuration(videos: any[] | undefined): number {
if (!videos || videos.length === 0) return 0;

let totalDuration = 0;
let validVideoCount = 0;

videos.forEach(video => {
  if (!video.duration) return;
  
  const match = video.duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  if (!match) return;

  const hours = parseInt(match[1]?.slice(0, -1) || '0');
  const minutes = parseInt(match[2]?.slice(0, -1) || '0');
  const seconds = parseInt(match[3]?.slice(0, -1) || '0');
  
  const totalSeconds = (hours * 3600) + (minutes * 60) + seconds;
  if (totalSeconds > 0) {
    totalDuration += totalSeconds;
    validVideoCount++;
  }
});

return validVideoCount > 0 ? Math.round(totalDuration / validVideoCount) : 0;
}

/**
* 週の平均配信回数を計算
* @param videos - アーカイブ動画の配列
* @returns 週の平均配信回数（小数点第1位まで）
*/
export function getAvgStreamsPerWeek(videos: any[] | undefined): number {
  if (!videos || videos.length === 0) return 0;
  
  // 最新と最古の配信日を取得（直近30日分のみを対象とする）
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const dates = videos
      .filter(v => v.published_at) // published_atがある動画のみ
      .map(v => new Date(v.published_at))
      .filter(date => date >= thirtyDaysAgo) // 直近30日分のみ
      .sort((a, b) => b.getTime() - a.getTime());

  if (dates.length === 0) return 0;
  
  // 週あたりの配信回数を計算（30日を4.285714週として計算）
  const streamsPerWeek = (dates.length / 30) * 7;
  
  // 小数点第1位で四捨五入
  return Math.round(streamsPerWeek * 10) / 10;
}

/**
* 動画の最高同時接続数を取得
* @param video - 動画オブジェクト
* @returns 最高同時接続数。live_statsがない場合は0
*/
export function getVideoMaxConcurrentViewers(video: any): number {
  if (!video.live_stats || Object.keys(video.live_stats).length === 0) {
      return 0;
  }
  return Math.max(...Object.values(video.live_stats));
}